import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  AddInvoicesModel,
  ProjectDeleteInput,
  ProjectDeleteSales,
  ProjectModel,
  ProjectMutationInput,
  ProjectUpdateInput,
  ProjectUpdateSales,
} from './model';

export function useCreateProject(
  options?: UseMutationOptions<
    ApiResult<ProjectModel>,
    ApiError,
    ProjectMutationInput
  >,
): UseMutationResult<ApiResult<ProjectModel>, ApiError, ProjectMutationInput> {
  return useMutation<ApiResult<ProjectModel>, ApiError, ProjectMutationInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: 'projects',
        method: 'POST',
        classType: ProjectModel,
        body,
      });
    },
    options,
  );
}

export function useUpdateProject(
  options?: UseMutationOptions<
    ApiResult<ProjectModel>,
    ApiError,
    ProjectUpdateInput
  >,
): UseMutationResult<ApiResult<ProjectModel>, ApiError, ProjectUpdateInput> {
  return useMutation<ApiResult<ProjectModel>, ApiError, ProjectUpdateInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `projects/${body.projectId}`,
        method: 'PUT',
        classType: ProjectModel,
        body: body.body,
      });
    },
    options,
  );
}

export function useCompleteProject(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ProjectDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ProjectDeleteInput> {
  return useMutation<{ message: string }, ApiError, ProjectDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `projects/${body.projectId}/complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useRevertCompleteProject(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ProjectDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ProjectDeleteInput> {
  return useMutation<{ message: string }, ApiError, ProjectDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `projects/${body.projectId}/revert-complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useCancelProject(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ProjectDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ProjectDeleteInput> {
  return useMutation<{ message: string }, ApiError, ProjectDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `projects/${body.projectId}/cancel`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useAddInvoice(
  options?: UseMutationOptions<
    ApiResult<AddInvoicesModel>,
    ApiError,
    ProjectUpdateSales
  >,
): UseMutationResult<
  ApiResult<AddInvoicesModel>,
  ApiError,
  ProjectUpdateSales
> {
  return useMutation<ApiResult<AddInvoicesModel>, ApiError, ProjectUpdateSales>(
    async function (body) {
      return await MutationFetchFunction({
        url: `projects/${body.projectId}/add-sale`,
        method: 'PUT',
        classType: AddInvoicesModel,
        body: body.body,
      });
    },
    options,
  );
}

export function useRemoveInvoice(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    ProjectDeleteSales
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  ProjectDeleteSales
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    ProjectDeleteSales
  >(async function (body) {
    return await MutationFetchFunction({
      url: `projects/${body.projectId}/remove-sale/${body.saleRefId}`,
      method: 'PATCH',
    });
  });
}
